import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea } from "@/components/FormBits";
import { api } from "@/lib/api";

const LISTS = [
  ["categories", "Project categories"],
  ["affected", "Who was affected options"],
  ["challenges", "Challenge options"],
  ["solutions", "Solution options"],
  ["outcomes", "Outcome options"],
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [emails, setEmails] = useState("");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [maxSize, setMaxSize] = useState(50);
  const [accepted, setAccepted] = useState("");
  const [hashtags, setHashtags] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestRecipients, setDigestRecipients] = useState("");
  const [submitRecipients, setSubmitRecipients] = useState("");
  const [digestSending, setDigestSending] = useState(false);
  const [newOpt, setNewOpt] = useState({});

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setS(r.data);
      setEmails((r.data.email_recipients || []).join(", "));
      setBody(r.data.email_body || "");
      setSubject(r.data.email_subject_prefix || "");
      setMaxSize(r.data.max_file_size_mb || 50);
      setAccepted((r.data.accepted_extensions || []).join(", "));
      setHashtags(!!r.data.social_hashtags_enabled);
      setDigestEnabled(r.data.weekly_digest_enabled !== false);
      setDigestRecipients((r.data.weekly_digest_recipients || []).join(", "));
      setSubmitRecipients((r.data.email_recipients || []).join(", "));
    });
  }, []);

  const saveBasics = async () => {
    const payload = {
      email_recipients: emails.split(",").map((e) => e.trim()).filter(Boolean),
      email_body: body,
      email_subject_prefix: subject,
      max_file_size_mb: Number(maxSize) || 50,
      accepted_extensions: accepted.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean),
      social_hashtags_enabled: hashtags,
    };
    const { data } = await api.patch("/admin/settings", payload);
    setS(data);
    toast.success("Settings saved");
  };

  const saveFooter = async (patch) => {
    const { data } = await api.patch("/admin/settings", patch);
    setS(data);
    toast.success("Footer updated");
  };

  const saveDigest = async () => {
    const { data } = await api.patch("/admin/settings", {
      weekly_digest_enabled: digestEnabled,
      weekly_digest_recipients: digestRecipients.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean),
      email_recipients: submitRecipients.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean),
    });
    setS(data);
    toast.success("Notification settings saved");
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const { data } = await api.post("/admin/digest/send-now", {});
      toast.success(`Digest sent to ${(data.sent_to || []).join(", ")} (${data.items} items)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setDigestSending(false); }
  };

  const addOption = async (list) => {
    const v = (newOpt[list] || "").trim();
    if (!v) return;
    const { data } = await api.post("/admin/settings/add-option", { list, value: v });
    setS(data);
    setNewOpt((m) => ({ ...m, [list]: "" }));
  };

  const removeOption = async (list, value) => {
    const next = (s[list] || []).filter((x) => x !== value);
    const { data } = await api.patch("/admin/settings", { [list]: next });
    setS(data);
  };

  if (!s) return <Layout><div className="py-20 text-center text-gray-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Link to="/admin" className="rcw-btn-ghost text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">Settings</h1>

        <div className="rcw-card p-6 mt-6 space-y-5" data-testid="settings-basics-card">
          <h2 className="font-display text-xl font-semibold">Email & uploads</h2>
          <Field label="Email recipients (comma-separated)">
            <Input value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="settings-emails" />
          </Field>
          <Field label="Email subject prefix">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="settings-subject" />
          </Field>
          <Field label="Email body template">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="settings-body" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Max file size (MB)">
              <Input type="number" value={maxSize} onChange={(e) => setMaxSize(e.target.value)} data-testid="settings-max-size" />
            </Field>
            <Field label="Accepted file extensions">
              <Input value={accepted} onChange={(e) => setAccepted(e.target.value)} data-testid="settings-accepted" />
            </Field>
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hashtags}
              onChange={(e) => setHashtags(e.target.checked)}
              className="h-4 w-4 accent-amber-500"
              data-testid="settings-hashtags-toggle"
            />
            <span className="text-sm text-gray-700">
              Include hashtags in AI-generated social posts <span className="text-gray-400">(emojis are never used)</span>
            </span>
          </label>
          <button onClick={saveBasics} className="rcw-btn-primary" data-testid="settings-save-basics">
            <Save className="h-4 w-4" /> Save settings
          </button>
        </div>

        <div className="rcw-card p-6 mt-5 space-y-4" data-testid="settings-digest-card">
          <h2 className="font-display text-xl font-semibold">Weekly update email</h2>
          <p className="text-sm text-gray-500">
            Every Monday at 09:00, recipients get a summary of which projects were added or updated in the past week and by who.
          </p>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={digestEnabled}
              onChange={(e) => setDigestEnabled(e.target.checked)}
              className="h-4 w-4 accent-amber-500"
              data-testid="settings-digest-toggle"
            />
            <span className="text-sm text-gray-700">Send the weekly digest</span>
          </label>
          <Field label="Weekly digest recipients (comma-separated)">
            <Input value={digestRecipients} onChange={(e) => setDigestRecipients(e.target.value)} data-testid="settings-digest-recipients" />
          </Field>
          <Field label="New submission alerts — who gets emailed when a project is submitted (comma-separated)">
            <Input value={submitRecipients} onChange={(e) => setSubmitRecipients(e.target.value)} data-testid="settings-submit-recipients" />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button onClick={saveDigest} className="rcw-btn-primary" data-testid="settings-digest-save">
              <Save className="h-4 w-4" /> Save digest settings
            </button>
            <button onClick={sendDigestNow} disabled={digestSending} className="rcw-btn-ghost" data-testid="settings-digest-send-now">
              {digestSending ? "Sending…" : "Send now (test)"}
            </button>
          </div>
        </div>

        {/* Footer / lower hero */}
        <FooterSection s={s} onSave={saveFooter} />

        {LISTS.map(([key, label]) => (
          <div key={key} className="rcw-card p-6 mt-5" data-testid={`settings-list-${key}`}>
            <h2 className="font-display text-xl font-semibold">{label}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {(s[key] || []).map((opt) => (
                <span key={opt} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-sm">
                  {opt}
                  <button onClick={() => removeOption(key, opt)} className="text-gray-400 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <input
                value={newOpt[key] || ""}
                onChange={(e) => setNewOpt((m) => ({ ...m, [key]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addOption(key)}
                placeholder="Add new…"
                className="rcw-input"
              />
              <button onClick={() => addOption(key)} className="rcw-btn-amber text-sm" data-testid={`settings-add-${key}`}>
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}

function FooterSection({ s, onSave }) {
  const [img, setImg] = useState(s.footer_image_url || "");
  const [opacity, setOpacity] = useState(s.footer_overlay_opacity ?? 0.7);
  const [accent, setAccent] = useState(s.footer_accent_color || "#F59E0B");
  const [heading, setHeading] = useState(s.footer_heading || "");
  const [text, setText] = useState(s.footer_text || "");
  const [btnFull, setBtnFull] = useState(s.footer_button_full_label || "");
  const [btnPhotos, setBtnPhotos] = useState(s.footer_button_photos_label || "");

  const save = () =>
    onSave({
      footer_image_url: img,
      footer_overlay_opacity: Number(opacity),
      footer_accent_color: accent,
      footer_heading: heading,
      footer_text: text,
      footer_button_full_label: btnFull,
      footer_button_photos_label: btnPhotos,
    });

  return (
    <div className="rcw-card p-6 mt-5 space-y-5" data-testid="settings-footer-card">
      <h2 className="font-display text-xl font-semibold">Animated footer / lower hero</h2>
      <Field label="Footer background image URL" hint="Public URL of a wide landscape image (drone or works photo).">
        <Input value={img} onChange={(e) => setImg(e.target.value)} data-testid="settings-footer-image" />
      </Field>
      {img ? (
        <div className="rounded-xl overflow-hidden border border-gray-200 aspect-[16/6] bg-gray-100">
          <img src={img} alt="" className="w-full h-full object-cover" />
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={`Overlay darkness (${Number(opacity).toFixed(2)})`}>
          <input
            type="range"
            min="0.2"
            max="0.95"
            step="0.05"
            value={opacity}
            onChange={(e) => setOpacity(e.target.value)}
            className="w-full accent-amber-500"
            data-testid="settings-footer-opacity"
          />
        </Field>
        <Field label="Accent line colour">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="h-11 w-14 rounded-md border border-gray-200 cursor-pointer"
              data-testid="settings-footer-accent"
            />
            <Input value={accent} onChange={(e) => setAccent(e.target.value)} />
          </div>
        </Field>
      </div>
      <Field label="Footer heading">
        <Input value={heading} onChange={(e) => setHeading(e.target.value)} data-testid="settings-footer-heading" />
      </Field>
      <Field label="Footer text">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="settings-footer-text" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Primary button label">
          <Input value={btnFull} onChange={(e) => setBtnFull(e.target.value)} data-testid="settings-footer-btn-full" />
        </Field>
        <Field label="Secondary button label">
          <Input value={btnPhotos} onChange={(e) => setBtnPhotos(e.target.value)} data-testid="settings-footer-btn-photos" />
        </Field>
      </div>
      <button onClick={save} className="rcw-btn-primary" data-testid="settings-save-footer">
        <Save className="h-4 w-4" /> Save footer
      </button>
    </div>
  );
}
